import { useEffect, useMemo, useState } from 'react';

import { fromApiBoolean, toApiBoolean } from '@liquidweb-dev/club-sauce';
import Box from '@liquidweb-dev/undercurrent/Box';
import Button from '@liquidweb-dev/undercurrent/Button';
import Dialog, { DialogProps } from '@liquidweb-dev/undercurrent/Dialog';
import DialogActions from '@liquidweb-dev/undercurrent/DialogActions';
import DialogContent from '@liquidweb-dev/undercurrent/DialogContent';
import DialogTitle from '@liquidweb-dev/undercurrent/DialogTitle';
import Fade from '@liquidweb-dev/undercurrent/Fade';
import BadgeCheckIcon from '@liquidweb-dev/undercurrent/icons/lucide/BadgeCheck';
import BotIcon from '@liquidweb-dev/undercurrent/icons/lucide/Bot';
import WrenchIcon from '@liquidweb-dev/undercurrent/icons/lucide/Wrench';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import toast from '@liquidweb-dev/undercurrent/toast';
import Tooltip from '@liquidweb-dev/undercurrent/Tooltip';

import LWForm, { FormSubmitHandler } from 'components/LWForm';
import HookedRadioGroup from 'components/standard/Form/HookedRadioGroup';
import HookedSubmit from 'components/standard/Form/HookedSubmit';
import WizardHat from 'components/standard/WizardHat';
import useExtractCSRDetails from 'modules/queries/ssl/certificate/useExtractCSRDetails';
import useSslCertificateGenerate from 'modules/queries/ssl/certificate/useGenerate';
import useSslCertificateHostServers from 'modules/queries/ssl/certificate/useHostSevers';
import useSslCertificateOrder from 'modules/queries/ssl/certificate/useOrder';
import useDebounce from 'utility/effects/useDebounce';
import useSendHubspotEvent from 'utility/gtm/hubspot/useEvent';
import AutomatedMethod from './AutomatedMethod';
import ManualMethod from './ManualMethod';
import MethodTile from './MethodTile';

export interface DialogAddSslProps extends Omit<DialogProps, 'onClose'> {
	onClose: () => void;
}

const initialFormValues: {
	autorenew: boolean;
	/** For automated method. City is determined by extracted CSR in manual method */
	city: string;
	/** For automated method. Country is determined by extracted CSR in manual method */
	country: string;
	/** For manual method, the CSR */
	csr: string;
	/** For automated method, the domain for the SSL */
	domain: string;
	/** For automated method. Email address is determined by extracted CSR in manual method */
	emailAddress: string;
	method: 'manual' | 'automated';
	/** For automated method. Organization is determined by extracted CSR in manual method */
	organization: string;
	/** For automated method. Organizational Unit is determined by extracted CSR in manual method */
	organizationalUnit: string;
	/** For automated method, the uniq_id of the server */
	server: string;
	/** For automated method. State is determined by extracted CSR in manual method */
	state: string;
	verification_method: 'auto' | 'dns' | 'metatag';
	/** For automated method. Wildcard is determined by extracted CSR in manual method */
	wildcard: boolean;
} = {
	autorenew: true,
	city: '',
	country: '',
	csr: '',
	domain: '',
	emailAddress: '',
	method: 'manual',
	organization: '',
	organizationalUnit: '',
	server: '',
	state: '',
	verification_method: 'dns',
	wildcard: false,
};

const createValidators = (method: 'manual' | 'automated') => {
	if (method === 'manual') {
		return {};
	}

	return {
		city: (value: string) => {
			if (!value) return 'Please provide a city';
			return '';
		},
		state: (value: string) => {
			if (!value) return 'Please provide a region';
			return '';
		},
		country: (value: string) => {
			if (!value) return 'Please provide a country';
			return '';
		},
		emailAddress: (value: string) => {
			if (!value) return 'Please provide an email address';
			return '';
		},
		organization: (value: string) => {
			if (!value) return 'Please provide an organization';
			return '';
		},
		organizationalUnit: (value: string) => {
			if (!value) return 'Please provide a department';
			return '';
		},
	};
};

const DialogAddSsl = ({ onClose, open, ...rest }: DialogAddSslProps) => {
	const [formValues, setFormValues] = useState(initialFormValues);
	const [formErrors, setFormErrors] = useState({});

	const debouncedCsr = useDebounce(formValues.csr, 500);

	const extractCSRDetailsEnabled = Boolean(debouncedCsr);
	const {
		data: extractedCsrDetails,
		isError: extractedCsrIsError,
		isFetching: extractCSRDetailsIsFetching,
	} = useExtractCSRDetails(
		{ csr: debouncedCsr },
		{ enabled: extractCSRDetailsEnabled },
	);
	const { data: hostServersData, isLoading: hostServersIsLoading } =
		useSslCertificateHostServers({ enabled: open });

	const { mutateAsync: orderCert, isLoading: orderCertIsLoading } =
		useSslCertificateOrder();
	const { mutateAsync: generateCert, isLoading: generateCertIsLoading } =
		useSslCertificateGenerate();

	const sendSslEvent = useSendHubspotEvent('ssl');

	const isAutoVerifyCapabale = fromApiBoolean(
		extractedCsrDetails?.autoVerifyCapable,
	);
	// if the CSR is auto verify capable, set the verification method to auto
	useEffect(() => {
		if (isAutoVerifyCapabale) {
			setFormValues((prev) => ({ ...prev, verification_method: 'auto' }));
		}
	}, [isAutoVerifyCapabale]);

	const commonName = extractedCsrDetails?.commonName;

	const disallowOrder = useMemo(() => {
		if (formValues.method === 'manual') {
			return (
				extractCSRDetailsIsFetching || // prefer `isFetching` to ensure we are displaying the correct extracted CSR details before allowing to order
				extractedCsrIsError ||
				!commonName
			);
		}

		if (formValues.method === 'automated') {
			return !formValues.domain;
		}

		return false;
	}, [
		formValues.method,
		formValues.domain,
		extractCSRDetailsIsFetching,
		extractedCsrIsError,
		commonName,
	]);

	const handleClose = () => {
		onClose();
		setFormErrors({});
		setFormValues(initialFormValues);
	};

	const handleSubmit: FormSubmitHandler<typeof initialFormValues> = async ({
		autorenew,
		city,
		country,
		csr,
		domain,
		emailAddress,
		method,
		organization,
		organizationalUnit,
		server,
		state,
		verification_method,
		wildcard,
	}) => {
		try {
			if (method === 'manual' && commonName) {
				await orderCert({
					csr,
					autorenew: toApiBoolean(autorenew),
					duration: 1,
					verification_method,
					domain: commonName,
				});
			}

			if (method === 'automated') {
				await generateCert({
					autorenew: toApiBoolean(autorenew),
					city,
					commonName: domain,
					country,
					duration: 1,
					emailAddress,
					organization,
					organizationalUnit,
					state,
					uniq_id: server,
					verification_method,
					wildcard: toApiBoolean(wildcard),
				});
			}

			toast.success('Your SSL certificate has been ordered');
			sendSslEvent();
			handleClose();
		} catch (_error) {
			toast.danger('It looks like there was a problem submitting your order');
		}
	};

	const handleFormChange = (newValues: typeof formValues) => {
		// If server changed, reset domain
		if (newValues.server !== formValues.server) {
			setFormValues({ ...newValues, domain: '' });
			return;
		}

		// reset verifiction method upon switching between manual and automated SSL generation
		if (newValues.method !== formValues.method) {
			setFormValues({
				...newValues,
				verification_method: newValues.method === 'manual' ? 'dns' : 'auto',
			});

			return;
		}

		setFormValues(newValues);
	};

	const canUseAutomatedMethod = Object.keys(hostServersData || {}).length > 0;

	return (
		<Dialog
			fullScreen
			open={open}
			onClose={handleClose}
			useSwipeableDrawer={false}
			{...rest}
		>
			<DialogTitle onClose={handleClose} />
			<LWForm
				values={formValues}
				onValuesChange={handleFormChange}
				errors={formErrors}
				onErrorsChange={setFormErrors}
				onSubmit={handleSubmit}
				style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
				validators={createValidators(formValues.method)}
			>
				<DialogContent
					sx={{
						maxWidth: '800px',
						marginX: 'auto',
						display: 'flex',
						flexDirection: 'column',
						gap: 600,
						flex: 1,
					}}
				>
					<WizardHat
						title="Order a certificate"
						description="Tell us where your content is coming from and how we can access it. You can add multiple sources if needed."
					/>
					<Stack>
						<HookedRadioGroup
							name="method"
							label="Method"
							sx={{ display: 'flex', flexDirection: { xs: 'row' }, gap: 200 }}
						>
							<MethodTile
								value="manual"
								checked={formValues.method === 'manual'}
								Icon={WrenchIcon}
								tooltipMessage="Automates verification only, CSR generation & SSL installation are manual"
							>
								Manual
							</MethodTile>

							<Tooltip
								variant="fancy"
								title={
									!canUseAutomatedMethod &&
									!hostServersIsLoading &&
									'What is this?'
								}
								message={
									!canUseAutomatedMethod && !hostServersIsLoading
										? 'It looks like you don’t have a sever with WHM/cPanel installed'
										: ''
								}
								arrow
								placement="top"
							>
								{/* forwarding a ref to RadioTile does not appear to work - wrapping it in a Box as a workaround */}
								<Box sx={{ flex: 1, width: '100%' }}>
									<MethodTile
										value="automated"
										checked={formValues.method === 'automated'}
										Icon={BotIcon}
										tooltipMessage="Automates CSR generation, verification & installation, WHM/cPanel required"
										disabled={!canUseAutomatedMethod || hostServersIsLoading}
									>
										Automated via WHM/cPanel
									</MethodTile>
								</Box>
							</Tooltip>
						</HookedRadioGroup>

						<Stack sx={{ position: 'relative' }}>
							<Fade
								style={{ width: '100%' }}
								in={formValues.method === 'manual'}
							>
								<ManualMethod
									autorenew={formValues.autorenew}
									csr={debouncedCsr}
									sx={{ mt: 500, position: 'absolute' }}
								/>
							</Fade>

							<Fade
								style={{ width: '100%' }}
								in={formValues.method === 'automated'}
							>
								<AutomatedMethod
									autorenew={formValues.autorenew}
									country={formValues.country}
									domain={formValues.domain}
									wildcard={formValues.wildcard}
									server={formValues.server}
									sx={{ mt: 500, position: 'absolute' }}
								/>
							</Fade>
						</Stack>
					</Stack>
				</DialogContent>
				<DialogActions
					sx={{
						paddingX: 200,
						paddingBottom: 200,
						justifyContent: 'space-between',
					}}
				>
					<Button size="large" variant="secondary" onClick={handleClose}>
						Cancel
					</Button>
					<HookedSubmit
						size="large"
						color="highlight"
						startIcon={<BadgeCheckIcon />}
						disabled={disallowOrder}
						loading={orderCertIsLoading || generateCertIsLoading}
					>
						Order certificate
					</HookedSubmit>
				</DialogActions>
			</LWForm>
		</Dialog>
	);
};

export default DialogAddSsl;
